import { useEffect, useState } from "react";
import api, { fmtTRY, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, Phone, Mail, Search, User as UserIcon, Wallet, Link as LinkIcon, Copy, RefreshCw, FileSpreadsheet } from "lucide-react";
import { downloadFile } from "../lib/download";

const empty = { name: "", phone: "", email: "", address: "", tax_no: "", notes: "", portal_enabled: true };

const TYPE_STYLES = {
  traktor_satisi: "bg-[#1B4D3E] text-white",
  servis: "bg-[#EA580C] text-white",
  parca_siparisi: "bg-[#D97706] text-white",
  tahsilat: "bg-emerald-600 text-white",
  senet_tahsilat: "bg-blue-600 text-white",
};

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [ledger, setLedger] = useState(null); // { customer, entries, totals }
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const load = () => api.get("/customers").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Ad Soyad zorunlu");
    try {
      if (editing) await api.put(`/customers/${editing}`, form);
      else await api.post("/customers", form);
      toast.success("Kaydedildi"); setOpen(false); setForm(empty); setEditing(null); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/customers/${id}`); toast.success("Silindi"); load();
  };

  const edit = (c) => { setEditing(c.id); setForm({ ...empty, ...c }); setOpen(true); };

  const openLedger = async (c) => {
    setLedgerLoading(true); setLedger({ customer: c, entries: [], totals: null });
    try {
      const { data } = await api.get(`/customers/${c.id}/ledger`);
      setLedger(data);
    } catch { toast.error("Cari hareketler yüklenemedi"); }
    setLedgerLoading(false);
  };

  const regenToken = async (c) => {
    const { data } = await api.post(`/customers/${c.id}/portal-token/regenerate`);
    toast.success("Portal linki yenilendi");
    setLedger((l) => l ? { ...l, customer: { ...l.customer, portal_token: data.portal_token } } : l);
    load();
  };

  const copyPortal = (token) => {
    const url = `${window.location.origin}/portal/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Portal linki panoya kopyalandı");
  };

  const filtered = items.filter((c) => (c.name + (c.phone || "")).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">CRM</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Müşteriler</h1>
        </div>
        <Button className="bg-[#1B4D3E] hover:bg-[#163e32] text-white gap-2" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-customer-btn">
          <Plus size={16} /> Yeni Müşteri
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Müşteri ara…" className="pl-9" data-testid="customer-search" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {filtered.map((c) => (
          <Card key={c.id} className="border-slate-200 tp-fade" data-testid={`customer-card-${c.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-10 w-10 rounded-full bg-[#1B4D3E]/10 text-[#1B4D3E] grid place-items-center font-bold">{c.name?.[0]?.toUpperCase() || <UserIcon size={16} />}</div>
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">{c.name}</div>
                    <div className="text-xs text-slate-500 truncate">{c.tax_no || "—"}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openLedger(c)} data-testid={`ledger-customer-${c.id}`} title="Cari Hareketler"><Wallet size={14} className="text-[#1B4D3E]" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => edit(c)} data-testid={`edit-customer-${c.id}`}><Pencil size={14} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`delete-customer-${c.id}`}><Trash2 size={14} className="text-rose-600" /></Button>
                </div>
              </div>
              <div className="text-sm text-slate-600 space-y-1">
                {c.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" />{c.phone}</div>}
                {c.email && <div className="flex items-center gap-2"><Mail size={14} className="text-slate-400" />{c.email}</div>}
                {c.address && <div className="text-xs text-slate-500 line-clamp-2">{c.address}</div>}
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">Kayıt bulunamadı.</div>}
      </div>

      {/* Edit/Add */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Müşteri Düzenle" : "Yeni Müşteri"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ad Soyad *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cust-phone" /></div>
              <div><Label>E-posta</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cust-email" /></div>
            </div>
            <div><Label>Vergi/TC No</Label><Input value={form.tax_no} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} data-testid="cust-tax" /></div>
            <div><Label>Adres</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="cust-address" /></div>
            <div><Label>Not</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cust-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={submit} data-testid="save-customer-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cari Hareketler */}
      <Dialog open={!!ledger} onOpenChange={(v) => !v && setLedger(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {ledger && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-3 pr-6">
                  <span>Cari Hareketler · {ledger.customer.name}</span>
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadFile(`/exports/customer-ledger/${ledger.customer.id}.xlsx`, `cari-ekstre-${ledger.customer.name}.xlsx`)} data-testid="export-ledger-btn">
                    <FileSpreadsheet size={14} /> Excel
                  </Button>
                </DialogTitle>
              </DialogHeader>
              {ledger.customer.portal_token && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <LinkIcon size={16} className="text-[#1B4D3E] shrink-0" />
                    <span className="text-xs text-slate-500">Müşteri Portalı Linki:</span>
                    <code className="font-mono text-xs bg-white border px-2 py-1 rounded truncate max-w-[280px]">{`${window.location.origin}/portal/${ledger.customer.portal_token}`}</code>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => copyPortal(ledger.customer.portal_token)} className="gap-1" data-testid="copy-portal"><Copy size={12} /> Kopyala</Button>
                    <Button size="sm" variant="outline" onClick={() => regenToken(ledger.customer)} className="gap-1" data-testid="regen-portal"><RefreshCw size={12} /> Yenile</Button>
                  </div>
                </div>
              )}
              {ledgerLoading && <div className="text-center py-6 text-slate-500">Yükleniyor…</div>}
              {ledger.totals && (
                <div className="grid grid-cols-3 gap-2 my-3">
                  <div className="bg-slate-900 text-white rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-slate-300">Toplam Borç</div>
                    <div className="text-xl font-extrabold">{fmtTRY(ledger.totals.borc)}</div>
                  </div>
                  <div className="bg-emerald-600 text-white rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-100">Toplam Alacak</div>
                    <div className="text-xl font-extrabold">{fmtTRY(ledger.totals.alacak)}</div>
                  </div>
                  <div className={`${ledger.totals.balance >= 0 ? "bg-rose-600" : "bg-[#1B4D3E]"} text-white rounded-lg p-3`}>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-white/80">Bakiye</div>
                    <div className="text-xl font-extrabold">{fmtTRY(Math.abs(ledger.totals.balance))} <span className="text-xs font-normal">{ledger.totals.balance >= 0 ? "borçlu" : "alacaklı"}</span></div>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Açıklama</TableHead>
                      <TableHead>Referans</TableHead>
                      <TableHead className="text-right">Borç</TableHead>
                      <TableHead className="text-right">Alacak</TableHead>
                      <TableHead className="text-right">Bakiye</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(ledger.entries || []).map((e, i) => (
                      <TableRow key={i} data-testid={`ledger-row-${i}`}>
                        <TableCell className="text-xs">{fmtDate(e.date)}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] mr-1 ${TYPE_STYLES[e.type] || "bg-slate-500 text-white"}`}>{e.type.replace("_", " ")}</Badge>
                          <span className="text-sm">{e.label}</span>
                        </TableCell>
                        <TableCell className="text-xs font-mono">{e.ref_no}</TableCell>
                        <TableCell className="text-right text-rose-600 font-semibold">{e.borc ? fmtTRY(e.borc) : "—"}</TableCell>
                        <TableCell className="text-right text-emerald-700 font-semibold">{e.alacak ? fmtTRY(e.alacak) : "—"}</TableCell>
                        <TableCell className={`text-right font-bold ${e.balance > 0 ? "text-rose-600" : "text-emerald-700"}`}>{fmtTRY(e.balance)}</TableCell>
                      </TableRow>
                    ))}
                    {(!ledger.entries || ledger.entries.length === 0) && !ledgerLoading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Cari hareket yok.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
